/**
 * Validation Utility Functions
 * 
 * Provides email validation and error parsing utilities
 */

/**
 * Validates email address format
 * More robust regex that catches spaces and invalid formats
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email is valid
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // Trim whitespace
  const trimmedEmail = email.trim();
  
  // Check for spaces
  if (trimmedEmail.includes(' ')) return false;
  
  // More comprehensive email regex
  // Allows: letters, numbers, dots, hyphens, underscores, plus signs before @
  // Domain: letters, numbers, dots, hyphens
  // TLD: 2-63 characters (letters only)
  const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
  
  return emailRegex.test(trimmedEmail);
};

/**
 * Parses AWS Cognito error messages and returns user-friendly messages
 * @param {Error|string} error - Error object or error message string
 * @returns {string} User-friendly error message
 */
export const parseAuthError = (error) => {
  if (!error) return 'Something went wrong!';
  
  let errorMessage = '';
  
  // If error is an object with message property
  if (typeof error === 'object' && error.message) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    return 'Something went wrong!';
  }
  
  // Try to parse JSON error messages
  try {
    // Check if error message is a JSON string
    if (errorMessage.startsWith('{') || errorMessage.startsWith('[')) {
      const parsed = JSON.parse(errorMessage);
      
      // Handle InvalidParameterException
      if (parsed.__type === 'InvalidParameterException') {
        if (parsed.message) {
          // Check for userAlias/userName validation errors
          if (parsed.message.includes('userAlias') || parsed.message.includes('userName')) {
            return 'Please enter a valid email address. Email cannot contain spaces or special characters.';
          }
          // Return the parsed message if it's user-friendly
          return parsed.message;
        }
      }
      
      // Return the message from parsed JSON if available
      if (parsed.message) {
        return parsed.message;
      }
    }
  } catch (e) {
    // Not JSON, continue with original error message
  }
  
  // Handle common AWS Cognito error messages
  const errorMappings = {
    'InvalidParameterException': 'Invalid input. Please check your email address.',
    'userAlias': 'Please enter a valid email address.',
    'userName': 'Please enter a valid email address.',
    'User is not confirmed.': 'Your account verification is incomplete. Please complete the verification before logging in.',
    'User does not exist.': 'We could not find your login details. If you have signed up earlier using social log in, then please follow the same.',
    'An account with the given email already exists.': 'An account with this email already exists.',
    'User is already confirmed.': 'An account is already registered. Please login.',
    'current status is CONFIRMED': 'An account is already registered. Please login.',
    'Cannot resend confirmation code': 'An account is already registered. Please login.',
    'PreSignUp failed with error': errorMessage.replace('PreSignUp failed with error ', ''),
  };
  
  // Check for known error patterns
  for (const [key, value] of Object.entries(errorMappings)) {
    if (errorMessage.includes(key)) {
      return value;
    }
  }
  
  // Remove "PreSignUp failed with error " prefix if present
  if (errorMessage.includes('PreSignUp failed with error ')) {
    return errorMessage.replace('PreSignUp failed with error ', '');
  }
  
  // Return original message if no mapping found
  return errorMessage || 'Something went wrong!';
};

